import { useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Check, ChevronsUpDown, UsersRound } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { HubLayout } from '@/components/layout/HubLayout';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { hubScopeFromParams } from '@/components/layout/hub-utils';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { ROUTES } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { ModelProvidersPage } from '@/pages/security/ModelProvidersPage';
import { PersonalApiKeysPage } from '@/pages/security/PersonalApiKeysPage';
import { SecretsPage } from '@/pages/security/SecretsPage';
import { OrgModelProvidersPage } from '@/pages/org/model-providers/OrgModelProvidersPage';
import { AdminSecretsPage } from '@/pages/admin/secrets/AdminSecretsPage';
import { TeamModelProvidersPage } from '@/pages/org/team-model-providers/TeamModelProvidersPage';
import { TeamSecretsPage } from '@/pages/org/team-secrets/TeamSecretsPage';
import {
  defaultSectionForCredentialsScope,
  sectionsForCredentialsScope,
  type CredentialsScope,
} from './credentials-hub-config';

const SCOPE_LABELS: Record<CredentialsScope, string> = {
  personal: 'Personal',
  team: 'Team',
  organization: 'Organization',
};

function CredentialsHubContent({ scope, section }: { scope: CredentialsScope; section: string }) {
  if (scope === 'personal') {
    if (section === 'model-providers') return <ModelProvidersPage embedded />;
    if (section === 'api-keys') return <PersonalApiKeysPage embedded />;
    if (section === 'secrets') return <SecretsPage embedded />;
  }

  if (scope === 'organization') {
    if (section === 'model-providers') return <OrgModelProvidersPage embedded />;
    if (section === 'secrets') return <AdminSecretsPage embedded />;
  }

  if (scope === 'team') {
    if (section === 'model-providers') return <TeamModelProvidersPage embedded />;
    if (section === 'secrets') return <TeamSecretsPage embedded />;
  }

  return null;
}

export function CredentialsHubPage() {
  const navigate = useNavigate();
  const slug = useActiveOrgSlug();
  const { scope: scopeParam, section: sectionParam, teamId } = useParams();
  const { activeMembership } = useAuth();
  const { activeOrganization } = useOrganization();

  const teams = activeMembership?.teams ?? [];
  const scope = hubScopeFromParams<CredentialsScope>({
    scopeParam,
    teamId,
    teamScopeValue: 'team',
    defaultScope: 'personal',
  });
  const section = sectionParam ?? defaultSectionForCredentialsScope(scope);

  const availableScopes = useMemo<CredentialsScope[]>(() => {
    const scopes: CredentialsScope[] = ['personal', 'organization'];
    if (teams.length > 0) scopes.splice(1, 0, 'team');
    return scopes;
  }, [teams.length]);

  if (!slug) {
    return <Navigate to={ROUTES.settings} replace />;
  }

  const orgRoutes = ROUTES.org(slug);

  if (scope === 'team' && !teamId && teams[0]) {
    // Preserve the requested section (e.g. secrets) so deep links and the
    // command palette can land on team secrets, not only model providers.
    return <Navigate to={orgRoutes.credentialsHubTeam(teams[0].id, section)} replace />;
  }

  if (scope === 'team' && teams.length === 0) {
    return (
      <Navigate
        to={orgRoutes.credentialsHubSection(
          'personal',
          defaultSectionForCredentialsScope('personal'),
        )}
        replace
      />
    );
  }

  const sections = sectionsForCredentialsScope(scope);
  const activeSection = sections.find((s) => s.id === section);

  if (!activeSection) {
    return (
      <Navigate
        to={
          scope === 'team' && teamId
            ? orgRoutes.credentialsHubTeam(teamId, defaultSectionForCredentialsScope('team'))
            : orgRoutes.credentialsHubSection(scope, defaultSectionForCredentialsScope(scope))
        }
        replace
      />
    );
  }

  const activeTeamId = scope === 'team' ? (teamId ?? teams[0]?.id) : undefined;
  const activeTeam = teams.find((t) => t.id === activeTeamId);

  function navigateScope(nextScope: CredentialsScope) {
    if (nextScope === 'team' && teams[0]) {
      navigate(
        orgRoutes.credentialsHubTeam(teams[0].id, defaultSectionForCredentialsScope('team')),
      );
      return;
    }
    navigate(
      orgRoutes.credentialsHubSection(nextScope, defaultSectionForCredentialsScope(nextScope)),
    );
  }

  function sectionHref(item: { id: string }): string {
    if (scope === 'team' && activeTeamId) {
      return orgRoutes.credentialsHubTeam(activeTeamId, item.id);
    }
    return orgRoutes.credentialsHubSection(scope, item.id);
  }

  return (
    <HubLayout
      title="Credentials"
      description="Model providers, API keys, and secrets across personal, team, and organization tiers."
      sections={sections}
      activeSectionId={section}
      sectionHref={sectionHref}
      navLabel="Credentials sections"
      scopes={{
        available: availableScopes,
        active: scope,
        labels: SCOPE_LABELS,
        onChange: (next) => navigateScope(next as CredentialsScope),
      }}
      headerSlot={
        <>
          {scope === 'organization' && activeOrganization && (
            <span className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground">
              <span className="flex size-4 items-center justify-center rounded bg-primary/20 text-3xs font-bold text-primary">
                {activeOrganization.name[0]}
              </span>
              {activeOrganization.name}
            </span>
          )}
          {scope === 'team' && teams.length > 0 && activeTeamId && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="group flex items-center gap-2.5 rounded-md border border-border bg-card px-3 py-1.5 text-left text-sm transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/60"
                  data-testid="credentials-team-switcher"
                  aria-label="Switch team"
                >
                  <UsersRound
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <PanelTitle as="span" size="sm" className="min-w-0 flex-1 truncate">
                    {activeTeam?.name ?? 'Select team'}
                  </PanelTitle>
                  <ChevronsUpDown
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
                    aria-hidden="true"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" sideOffset={6}>
                <DropdownMenuLabel className="-mx-1 -mt-1 mb-1 border-b border-border bg-muted/60 px-2.5 py-1.5 text-2xs font-medium uppercase tracking-wider text-muted-foreground">
                  {teams.length > 1 ? 'Switch team' : 'Team'}
                </DropdownMenuLabel>
                {teams.map((team) => {
                  const active = team.id === activeTeamId;
                  return (
                    <DropdownMenuItem
                      key={team.id}
                      onSelect={() => navigate(orgRoutes.credentialsHubTeam(team.id, section))}
                      className="gap-2"
                      data-testid={`credentials-team-item-${team.id}`}
                      data-active={active ? 'true' : undefined}
                    >
                      <UsersRound
                        className="h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 truncate text-sm">{team.name}</span>
                      {active && (
                        <Check
                          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      }
    >
      <HubReadGuard
        permission={activeSection.permission}
        team={scope === 'team' ? activeTeamId : undefined}
      >
        <CredentialsHubContent scope={scope} section={section} />
      </HubReadGuard>
    </HubLayout>
  );
}
